import { useContext, useRef } from 'react';

import { ApplicationContext } from '@src/app/contexts/Application';
import { DebtConContext } from '@src/app/contexts/DebtCon';
import {
    DebtType,
    debtTypeConfig,
    getApplicantName,
} from '@src/shared/helpers/debtCon';
import BackButton from '@src/ui/components/BackButton/BackButton';
import CoinsIcon from '@src/ui/components/Icons/CoinsIcon';

import DebtInputForm from './DebtInputForm';

interface DateInfoProps {
    label: string;
    date: string | null;
    className?: string;
}

const DebtInputView = () => {
    const { applicants } = useContext(ApplicationContext);
    const {
        currentDebtIndex,
        setCurrentDebtIndex,
        creditCommitmentsData,
        currentDebt,
    } = useContext(DebtConContext);

    const topRef = useRef<HTMLDivElement>(null);

    const debtConfig = debtTypeConfig[currentDebt.type as DebtType] || {
        icon: <CoinsIcon />,
        subtext: null,
    };

    const progress = Math.floor(
        ((currentDebtIndex + 1) / creditCommitmentsData.length) * 100,
    );

    const applicantName = getApplicantName(
        applicants.length,
        currentDebt,
        applicants,
    );

    const DateInfo = ({ label, date, className = '' }: DateInfoProps) => (
        <p
            className={`text-xs flex flex-col bg-periwinkle-100 w-1/2 p-2 rounded ${className}`}
        >
            <span className='font-bold text-midnight-100'>{label}</span>
            <span className='text-sm font-bold text-midnight-400'>
                {date || 'NA'}
            </span>
        </p>
    );

    return (
        <div
            className='text-steel-100 flex flex-col mx-auto max-w-400 w-full lg:max-w-none'
            ref={topRef}
        >
            <div className='relative flex items-center lg:mb-12 flex-row'>
                <div className='hidden lg:block lg:absolute lg:left-0'>
                    <BackButton />
                </div>

                <div className='flex items-center w-full my-4 lg:w-400 lg:mx-auto'>
                    <span className='font-sora-bold text-periwinkle-900 text-xs mr-4'>{`${
                        currentDebtIndex + 1
                    } of ${creditCommitmentsData.length}`}</span>

                    <div className='h-2 bg-monochrome-100 text-center rounded-full flex-grow'>
                        <div
                            className='h-full bg-periwinkle-900 opacity-70 rounded-full'
                            style={{ width: `${progress}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            <div
                className='p-4 lg:p-6 rounded-lg bg-monochrome-100 debt-shadow lg:w-3/4 lg:mx-auto flex flex-col lg:flex-row'
                style={{
                    boxShadow:
                        '0px 0px 4px 0px rgba(0, 0, 0, 0.15), 0px 0px 0px 5px rgba(255, 255, 255, 0.90)',
                }}
            >
                <div className='lg:mr-10 lg:w-1/2'>
                    {applicantName && (
                        <p className='text-sm font-bold mb-3'>
                            {applicantName}
                        </p>
                    )}

                    <div className='flex w-full'>
                        <span className='mr-2'>{debtConfig.icon}</span>
                        <div className='w-full'>
                            <p className='text-lg font-bold leading-120%'>
                                {currentDebt.type}
                            </p>
                            {debtConfig.subtext && (
                                <p className='text-sm leading-120%'>
                                    {debtConfig.subtext}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className='flex w-full mt-3 mb-4'>
                        <DateInfo
                            label='Start date'
                            date={currentDebt.startDate}
                            className='mr-2'
                        />
                        <DateInfo label='End date' date={currentDebt.endDate} />
                    </div>
                </div>
                <DebtInputForm
                    currentDebt={currentDebt}
                    currentDebtIndex={currentDebtIndex}
                    setCurrentDebtIndex={setCurrentDebtIndex}
                    topRef={topRef}
                    debts={creditCommitmentsData}
                />
            </div>
        </div>
    );
};

export default DebtInputView;
